import {useEffect, useMemo} from 'react';

import {useDispatch, useSelector} from 'react-redux';
import {InjectedRouter} from 'react-router';

import {useCurrentUserState} from 'app/current-user/api';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';
import {shallowEqual} from 'app/utils/react-utils';

import {getStartEndIntervalFromParams} from './interval-filter';
import {setAllTasksFilters} from './slice';

import type {
  TaskDashboardLocationParamsType,
  TasksFetchParamsType,
} from './types';
import type {Location} from 'history';

// this hook handles the all-tasks page redirection dance, as:
// 1. it should restore filters stored on redux store when mounted
// 2. it should save the filters being used to the redux store
// 3. it should let the page component it's being redirected so no fetch is made
// 4. it should convert the dashboard filters to proper fetch params
export function useAllTasksDashboardFilters(
  {query, pathname}: Location<TaskDashboardLocationParamsType>,
  router: InjectedRouter
): {
  redirecting: boolean;
  viewingAll: boolean;
  fetchParams: TasksFetchParamsType;
} {
  const dispatch = useDispatch();
  const {timezone} = useCurrentUserState().data!;
  const storedFilters = useSelector(
    (state) => state.tasks.dashboard.allTasksFilters
  );

  const {filtersWereUpdated, viewingAll: viewingAll} = useMemo(() => {
    return {
      filtersWereUpdated: fetchParamsAreDifferent(
        query,
        storedFilters,
        timezone
      ),
      viewingAll: !fetchParamsAreDifferent(query, {}, timezone),
    };
  }, [query, storedFilters, timezone]);

  // if:
  //   1. The page is being mounted
  //   2. no filters are being used
  //   3. there are filters stored on redux store,
  // we assume last used filters should be applied
  const shouldRedirect = viewingAll && filtersWereUpdated;

  const fetchParams = convertLocationParamsToFetchParams(query, timezone);

  useEffect(() => {
    // create a new object with prototype as react-router params has no prototype
    // and redux-toolkit serializable middleware complains about that
    // https://github.com/SweetProcess/sweetprocess.com/pull/7589#issuecomment-921808363
    dispatch(setAllTasksFilters({...query}));
  }, [query]);

  useEffect(() => {
    if (shouldRedirect) {
      router.replace({pathname, query: storedFilters});
    }
  }, []);

  return {
    redirecting: shouldRedirect,
    viewingAll,
    fetchParams,
  };
}

export const convertLocationParamsToFetchParams = (
  query: TaskDashboardLocationParamsType,
  userTz: string
): TasksFetchParamsType => ({
  page: query.page != null ? query.page : '1',
  page_size: query.page_size != null ? query.page_size : DEFAULT_PAGE_SIZE,
  due__gte: query.start,
  due__lte: query.end,
  user: query.user,
  assigner: query.assigner,
  template_id: query.template_id,
  completed: query.completed,
  content_type: query.content_type,
  object_id: query.object_id,
  ordering: query.ordering,
  team_id:
    query.team_id != null && query.team_id !== '-1' ? query.team_id : undefined,
  role_id:
    query.role_id != null && query.role_id !== '-1' ? query.role_id : undefined,
  in_no_teams: query.team_id === '-1' ? '1' : undefined,
  search: query.search != null ? query.search : undefined,
  folder: query.folder,
  ...getStartEndIntervalFromParams(query, userTz),
});

export const fetchParamsAreDifferent = (
  params: TaskDashboardLocationParamsType,
  nextParams: TaskDashboardLocationParamsType,
  userTz: string
): boolean => {
  const fetchParams = convertLocationParamsToFetchParams(params, userTz);
  const nextFetchParams = convertLocationParamsToFetchParams(
    nextParams,
    userTz
  );
  return !shallowEqual(fetchParams, nextFetchParams);
};
